import React, { useState } from 'react';
import bgImage from './assets/bg.jpeg';
import plusLogo from './assets/plus-logo.png';
import hospitalLogo from './hospital-logo.png';
import { clearPharmacyDraftState } from './utils/pharmacySession';

const SUPPORTED_TARGETS = new Set(['main', 'opd', 'pathology', 'radiology', 'pharmacy', 'admin', 'azad', 'my_portal']);

function canonicalIdentity(value) {
  const v = String(value || '').trim().toLowerCase();
  if (!v) return '';
  const at = v.indexOf('@');
  if (at === -1) return v.replace(/[._\-\s]+/g, '');
  const local = v.slice(0, at).replace(/[._\-\s]+/g, '');
  const domain = v.slice(at + 1);
  return `${local}@${domain}`;
}

function isKnownSuperAdminIdentity(user) {
  const configuredSuperEmail = String(import.meta.env.VITE_SUPERADMIN_EMAIL || '').trim().toLowerCase();
  const configuredSuperUsername = String(import.meta.env.VITE_SUPERADMIN_USERNAME || '').trim().toLowerCase();
  const identity = String(user?.email || user?.username || '').trim().toLowerCase();
  const canonical = canonicalIdentity(identity);
  if (configuredSuperEmail && identity === configuredSuperEmail) return true;
  if (configuredSuperUsername && identity === configuredSuperUsername) return true;
  if (configuredSuperEmail && canonical === canonicalIdentity(configuredSuperEmail)) return true;
  if (configuredSuperUsername && canonical === canonicalIdentity(configuredSuperUsername)) return true;
  return false;
}

function isPrivilegedRole(roleValue) {
  const role = String(roleValue || '').toLowerCase();
  return role.includes('super') || role.includes('owner') || role.includes('company admin') || role.includes('administrator');
}

function isSuperAdminUser(user) {
  const role = String(user?.role || user?.roles || '').toLowerCase().replace(/[^a-z0-9]+/g, '_');
  if (user?.is_superadmin === true || user?.isSuperAdmin === true) return true;
  if (Number(user?.is_superadmin) === 1 || Number(user?.isSuperAdmin) === 1) return true;
  if (isKnownSuperAdminIdentity(user)) return true;
  return role === 'superadmin' || role === 'super_admin' || role.includes('superadmin') || role.includes('super_admin');
}

function roleBasedTarget(roleValue) {
  const role = String(roleValue || '').toLowerCase();
  if (!role) return 'main';
  if (role.includes('pharmacy') || role.includes('pharmacist')) return 'pharmacy';
  if (role.includes('pathology') || role.includes('laboratory') || role.includes('lab')) return 'pathology';
  if (role.includes('radiology') || role.includes('radiologist') || role.includes('imaging')) return 'radiology';
  if (role.includes('doctor') || role.includes('consultant')) return 'my_portal';
  if (role.includes('opd')) return 'opd';
  if (isPrivilegedRole(role)) return 'main';
  return 'main';
}

function isDoctorRole(roleValue) {
  const role = String(roleValue || '').toLowerCase();
  return role.includes('doctor') || role.includes('consultant');
}

function normalizeModuleCode(raw) {
  const s = String(raw || '').toLowerCase().trim();
  if (!s) return null;
  if (s.includes('patholog') || s.includes('lab')) return 'pathology';
  if (s.includes('pharmac')) return 'pharmacy';
  if (s.includes('opd') || s.includes('out door') || s.includes('outdoor')) return 'opd';
  if (s.includes('ipd') || s.includes('in door') || s.includes('indoor')) return 'ipd';
  if (s.includes('patient') && s.includes('record')) return 'patient_record';
  if (s.includes('blood')) return 'blood_bank';
  if (s.includes('operation') || s.includes('theatre') || s.includes('ot')) return 'operation_theatre';
  if (s.includes('icu') || s.includes('hdu')) return 'hdu_icu';
  if (s.includes('dental') || s.includes('dentistry')) return 'dentistry';
  if (s.includes('admin')) return 'admin';
  return s.replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
}

function pickModuleValue(item) {
  if (typeof item === 'string' || typeof item === 'number') return item;
  if (!item || typeof item !== 'object') return null;
  return (
    item.code
    || item.module_code
    || item.module_name
    || item.module_key
    || item.name
    || item.key
    || item.moduleId
    || (item.module && (item.module.code || item.module.module_code || item.module.module_name || item.module.module_key || item.module.name || item.module.key || item.module.moduleId))
  );
}

async function fetchJsonWithTimeout(url, options = {}, timeoutMs = 12000) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...options, signal: controller.signal });
    const data = await res.json().catch(() => ({}));
    return { res, data };
  } finally {
    clearTimeout(timer);
  }
}

export default function SignIn({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [remember, setRemember] = useState(false);
  const [unlockInputs, setUnlockInputs] = useState(false);

  React.useEffect(() => {
    try {
      const remembered = localStorage.getItem('hmis_login_remember') === '1';
      const identifier = localStorage.getItem('hmis_login_identifier') || '';
      setRemember(remembered);
      if (remembered && identifier) setEmail(identifier);
    } catch (e) {}
  }, []);

  const submit = async (e) => {
    e && e.preventDefault();
    setError('');
    setLoading(true);
    try {
      try { if (typeof window !== 'undefined') window.companyProfile = null; } catch (e) {}
      try { localStorage.removeItem('hmis_token'); } catch (e) {}
      // backend expects username; send email in username field for compatibility
      let loginResp = await fetchJsonWithTimeout('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: email, password })
      });
      let res = loginResp.res;
      let j = loginResp.data;
      if (!res.ok) {
        // if server returned invalid credentials, try to resolve email -> username and retry
        if (res.status === 401 && email && email.indexOf('@') !== -1) {
          try {
            const usersLookup = await fetchJsonWithTimeout('/api/users', {}, 8000);
            const usersRes = usersLookup.res;
            if (usersRes.ok) {
              const users = usersLookup.data;
              const matched = Array.isArray(users) ? users.find(u => String(u.email || '').toLowerCase() === String(email).toLowerCase()) : null;
              if (matched && matched.username) {
                // retry login with the resolved username
                loginResp = await fetchJsonWithTimeout('/api/login', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ username: matched.username, password })
                });
                res = loginResp.res;
                j = loginResp.data;
              }
            }
          } catch (ee) {
            // ignore lookup errors
          }
        }
        if (!res.ok) {
          setError(j && j.error ? j.error : 'Login failed');
          setLoading(false);
          return;
        }
      }
      if (j && j.user) {
        const issuedToken = typeof j.token === 'string' ? j.token.trim() : '';
        const looksLikeJwt = issuedToken.split('.').length === 3;
        if (!looksLikeJwt) {
          try { localStorage.removeItem('hmis_token'); localStorage.removeItem('hmis_user'); } catch (e) {}
          setError('Login succeeded but no valid session token was returned.');
          setLoading(false);
          return;
        }
        let sessionUser = j.user;
        try { localStorage.setItem('hmis_user', JSON.stringify(sessionUser)); } catch (e) {}
        try { localStorage.setItem('hmis_token', issuedToken); } catch (e) {}

        // If server has flagged this account as SuperAdmin, ensure we clear any tenant context
        // so the client does not remain scoped to a specific tenant (e.g., Alhayat).
        const isSuperFromServer = !!(j.user && (j.user.is_superadmin === true || j.user.isSuperAdmin === true));
        if (isSuperFromServer) {
          sessionUser.tenant_id = null;
          sessionUser.tenantId = null;
          sessionUser.company_code = null;
          sessionUser.is_superadmin = true;
          sessionUser.isSuperAdmin = true;
          sessionUser.role = sessionUser.role || 'superadmin';
          try { localStorage.setItem('hmis_user', JSON.stringify(sessionUser)); } catch (e) {}
          try { localStorage.removeItem('hmis_single_module'); } catch (e) {}
          try { localStorage.removeItem('visibleModules'); } catch (e) {}
        }
        // Prevent previous user's module list from affecting this login session.
        try { localStorage.removeItem('visibleModules'); } catch (e) {}
        clearPharmacyDraftState();
        
        // Decide landing page from role + tenant module access.
        const userRole = j.user && j.user.role;
        const doctorUser = isDoctorRole(userRole);
        // Only pure super/owner accounts skip module-based routing; company admins follow module assignment
        const isSuperAdmin = isSuperAdminUser(j.user || {});
        let targetPage = roleBasedTarget(userRole);

        // Fallback to modules included in login payload when available.
        const loginModules = Array.isArray(j.user && j.user.modules)
          ? j.user.modules.map(d => {
              const raw = pickModuleValue(d);
              return normalizeModuleCode(raw);
            }).filter(Boolean)
          : [];
        if (doctorUser) {
          targetPage = 'my_portal';
        } else if (loginModules.length === 1 && !isSuperAdmin) {
          targetPage = loginModules[0];
        } else if (loginModules.length > 1 && targetPage !== 'main' && !loginModules.includes(targetPage)) {
          targetPage = 'main';
        }

        try {
          const token = issuedToken || localStorage.getItem('hmis_token');
          if (token) {
            const tenantId = j.user && (j.user.tenant_id || j.user.tenantId || j.user.company_code) ? (j.user.tenant_id || j.user.tenantId || j.user.company_code) : null;
            const tenantQs = tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : '';
            const modsLookup = await fetchJsonWithTimeout('/api/tenant/modules' + tenantQs, { headers: { Authorization: 'Bearer ' + token } }, 8000);
            const mres = modsLookup.res;
            if (mres.ok) {
              const mods = modsLookup.data;
              const codes = Array.isArray(mods) ? mods.map(d => {
                const raw = pickModuleValue(d);
                return normalizeModuleCode(raw);
              }).filter(Boolean) : [];
              if (codes.length) {
                sessionUser = { ...sessionUser, modules: codes };
                try { localStorage.setItem('hmis_user', JSON.stringify(sessionUser)); } catch (e) {}
              }
              if (isSuperAdmin) {
                targetPage = 'main';
              }
              if (doctorUser) {
                targetPage = 'my_portal';
              } else if (codes.length === 1 && !isSuperAdmin) {
                // Single module tenant: go directly to that module (company admins included).
                targetPage = codes[0];
              } else if (codes.length > 1 && targetPage !== 'main') {
                // Multi-module tenant: keep role target only if module is assigned.
                if (!codes.includes(targetPage)) {
                  targetPage = 'main';
                }
              }
            }
          }
        } catch (e) {
          // keep role/login-modules fallback if module lookup fails
        }

        // Ensure admins and doctors always land on the dashboard/main page
        try {
          const roleLower = String(userRole || '').toLowerCase();
          const isAdminRole = roleLower.includes('admin');
          if (isAdminRole || doctorUser) {
            targetPage = 'main';
          }
        } catch (e) {}

        if (!SUPPORTED_TARGETS.has(targetPage)) {
          targetPage = 'main';
        }

        targetPage = 'main';
        
        try {
          if (remember) {
            localStorage.setItem('hmis_login_remember', '1');
            localStorage.setItem('hmis_login_identifier', String(email || '').trim());
          } else {
            localStorage.removeItem('hmis_login_remember');
            localStorage.removeItem('hmis_login_identifier');
          }
        } catch (e) {}

        if (onLogin) onLogin(sessionUser, targetPage);
      }
    } catch (err) {
      if (err && err.name === 'AbortError') {
        setError('Login request timed out. Check if backend API on port 5000 is reachable.');
      } else {
        setError((err && err.message) || 'Login failed');
      }
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <div style={{ width: '100%', maxWidth: 1200, margin: '0 auto', display: 'flex', flexDirection: 'column', paddingTop: 0 }}>
        <div style={{ position: 'fixed', left: 0, right: 0, top: 0, zIndex: 60 }}>
          <div style={{ background: 'rgba(16,62,125,0.92)', color: '#fff', padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
              <div style={{ width: 44, height: 44, borderRadius: 22, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={plusLogo} alt="icon" style={{ width: 32, height: 32 }} />
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, letterSpacing: 0.6 }}>HOSPITAL MANAGEMENT INFORMATION SYSTEM</div>
            {/* right-side logo removed per design */}
          </div>
          <div style={{ height: 4, background: '#ef4444', width: '100%' }} />
        </div>

        <div style={{ display: 'flex', flex: 1, padding: 40, alignItems: 'center', minHeight: 'calc(100vh - 92px)' }}>
          <div style={{ flex: 1 }} />
          <form onSubmit={submit} autoComplete="on" onMouseDown={() => setUnlockInputs(true)} onFocusCapture={() => setUnlockInputs(true)} style={{ width: 420, background: 'linear-gradient(135deg, rgba(16,62,125,0.14), rgba(8,30,80,0.08))', padding: 22, borderRadius: 12, boxShadow: '0 12px 40px rgba(16,62,125,0.18)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.12)' }}>
            <h2 style={{ margin: 0, marginBottom: 12, color: '#0f4978', textAlign: 'center', fontWeight: 800, fontSize: '18px' }}>LOGIN</h2>
            {error && <div style={{ color: '#ffb4b4', marginBottom: 8 }}>{error}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input id="hmis-signin-identifier" name="username" autoComplete="username" autoCorrect="off" autoCapitalize="none" spellCheck={false} readOnly={!unlockInputs} value={email} onChange={e => setEmail(e.target.value)} onFocus={() => setUnlockInputs(true)} placeholder="Email or Username" style={{ padding: 12, borderRadius: 8, border: '1px solid rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.95)', color: '#0f4978' }} />
              <input id="hmis-signin-password" name="password" autoComplete="current-password" readOnly={!unlockInputs} value={password} onChange={e => setPassword(e.target.value)} onFocus={() => setUnlockInputs(true)} placeholder="Password" type="password" style={{ padding: 12, borderRadius: 8, border: '1px solid rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.95)', color: '#0f4978' }} />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#fff', fontSize: 13 }}>
                  <input type="checkbox" checked={remember} onChange={e => setRemember(Boolean(e.target.checked))} style={{ width: 15, height: 15, minWidth: 15, minHeight: 15, padding: 0, margin: 0, display: 'inline-block', verticalAlign: 'middle', boxSizing: 'border-box' }} />
                  <span>Remember me</span>
                </label>
                <a href="#" onClick={e => { e.preventDefault(); alert('Forgot password? Contact support.'); }} style={{ color: '#e0f2ff', fontSize: 13 }}>Forgot password?</a>
              </div>

              <div style={{ display: 'block', marginTop: 6 }}>
                <button type="submit" disabled={loading} style={{ width: '100%', padding: '12px 14px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700 }}>{loading ? 'Signing in...' : 'Sign in'}</button>
              </div>

              
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
